import { useState } from 'react';
import type { Page } from '@connaro/shared';
import { ApiError, api } from '../api/client';
import { Banner, EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Site {
  id: string;
  name: string;
}

interface ClaimCode {
  id: string;
  label: string;
  siteId: string;
  autoApprove: boolean;
  expiresAt: string;
  redeemedAt: string | null;
  revokedAt: string | null;
}

function status(code: ClaimCode, t: (k: string) => string): string {
  if (code.revokedAt) return t('registration.claimCodeRevoked');
  if (code.redeemedAt) return t('registration.claimCodeRedeemed');
  if (new Date(code.expiresAt).getTime() < Date.now()) return t('registration.claimCodeExpires');
  return '—';
}

/**
 * Claim codes for installers.
 *
 * The issued code is shown once, in large type, because the next thing that
 * happens is somebody reads it off this screen and types it on a different
 * keyboard. It is never retrievable afterwards: only its hash is stored.
 */
export function RegistrationPage(): JSX.Element {
  const t = useT();
  const sites = useAsync(() => api.get<Page<Site>>('/sites?limit=100'), []);
  const codes = useAsync(() => api.get<ClaimCode[]>('/claim-codes'), []);
  const [label, setLabel] = useState('');
  const [siteId, setSiteId] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      const res = await api.post<{ code: string }>('/claim-codes', { label, siteId, autoApprove });
      setIssued(res.code);
      setLabel('');
      codes.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
      // A failed issue must not leave the previous code on screen looking current.
      setIssued(null);
    }
  };

  const onRevoke = async (id: string): Promise<void> => {
    await api.del(`/claim-codes/${id}`);
    codes.reload();
  };

  const siteOptions = sites.data?.items ?? [];
  const siteName = (id: string): string => siteOptions.find((s) => s.id === id)?.name ?? id;

  return (
    <section>
      <div className="page-head">
        <h1>{t('registration.claimCodes')}</h1>
      </div>

      <div className="card">
        {issued ? (
          <Banner variant="warning">
            <div>{t('registration.claimCodeOnce')}</div>
            <code className="claim-code">{issued}</code>
            <div className="muted">{t('registration.claimCodeHint')}</div>
          </Banner>
        ) : null}
        {error ? <Banner variant="error">{error}</Banner> : null}

        <form className="form form--inline" onSubmit={(e) => void onCreate(e)}>
          <label className="field">
            <span>{t('enrollment.label')}</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} required />
          </label>
          <label className="field">
            <span>{t('nav.sites')}</span>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
              <option value="">—</option>
              {siteOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('enrollment.autoApprove')}</span>
            <input
              type="checkbox"
              checked={autoApprove}
              onChange={(e) => setAutoApprove(e.target.checked)}
            />
          </label>
          <button type="submit" className="btn">
            {t('registration.claimCodeCreate')}
          </button>
        </form>
      </div>

      {codes.loading ? (
        <Loading />
      ) : codes.error ? (
        <ErrorState message={codes.error.message} onRetry={codes.reload} />
      ) : !codes.data || codes.data.length === 0 ? (
        <EmptyState hint={t('registration.claimCodeEmpty')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('enrollment.label')}</th>
                <th>{t('nav.sites')}</th>
                <th>{t('registration.claimCodeExpires')}</th>
                <th>{t('enrollment.autoApprove')}</th>
                <th aria-label="status" />
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {codes.data.map((code) => {
                const spent = Boolean(code.revokedAt || code.redeemedAt);
                return (
                  <tr key={code.id}>
                    <td>{code.label}</td>
                    <td>{siteName(code.siteId)}</td>
                    <td>{new Date(code.expiresAt).toLocaleString()}</td>
                    <td>{code.autoApprove ? '✓' : '—'}</td>
                    <td>
                      <span className="chip">{status(code, t)}</span>
                    </td>
                    <td className="row-actions">
                      {spent ? null : (
                        <button
                          type="button"
                          className="btn btn--ghost btn--danger"
                          onClick={() => void onRevoke(code.id)}
                        >
                          {t('enrollment.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
